/**
 * Validación de stock vía Shopify App Proxy (/apps/stock-check) antes de redirigir
 * al checkout. Fail-open ante errores de red, 5xx o respuestas degradadas.
 *
 * Contrato: docs/contracts/stock-check-storefront-contract.md
 *
 * API pública (window.StockCheck):
 *   - interceptCheckout(proceedFn): valida el carrito actual y ejecuta proceedFn si OK.
 *   - registerCheckoutButton(btn): marca un botón como parte del flujo (overlay/disabled).
 *
 * Máquina de estados:
 *   idle → click → in-flight → (ok) idle + proceed
 *                           → (bloqueado) blocked + popup
 *   blocked → cartUpdate → localCheck → (ok) idle + re-habilitar botones
 *
 * Soft block (lanzamiento ventiladores): si el carrito combina bundle +
 * ventilador, se bloquea ANTES del stock-check con un popup informativo.
 * No persiste estado: cada click re-valida contra el carrito vigente.
 *
 * Con la setting ct_stock_check_enabled apagada se mantiene el soft block
 * pero no se llama a SAP.
 */

(function () {
  'use strict';

  const ENDPOINT = '/apps/stock-check';
  const CHUNK_SIZE = 50;
  const REQUEST_TIMEOUT_MS = 7000;
  const STOREFRONT_API_VERSION = '2026-01';
  const STOREFRONT_ENDPOINT = `${window.Shopify.routes.root}api/${STOREFRONT_API_VERSION}/graphql.json`;
  // Clasificación centralizada en ff-product-type.js (cargado antes en theme.liquid).
  // Fallback con el mismo criterio por si cambia el orden de scripts: un fallo aquí
  // degradaría el checkout en silencio (fail-open).
  const FFType = window.FFProductType || {
    PRODUCT_TYPES: { SERVICE: 'servicios', VENTILADOR: 'ventilador' },
    isBundle: (item) => Boolean(item && item.has_components),
    isService: (item) => ((item && item.product_type) || '').trim().toLowerCase() === 'servicios',
    isVentilador: (item) => ((item && item.product_type) || '').trim().toLowerCase() === 'ventilador',
  };
  const SERVICE_PRODUCT_TYPE = FFType.PRODUCT_TYPES.SERVICE;

  const state = {
    status: 'idle', // 'idle' | 'in-flight' | 'blocked'
    blockingItems: [], // [{ sku, requested, available, reason, displayTitle?, displaySku? }]
  };

  let bundleDisplayBySku = new Map();

  /**
   * @param {Array<number|string>} variantIds variant_ids de los items con has_components
   * @returns {Promise<Map<string, string>>} Map de variantId → SKU del primer componente
   */
  async function resolveBundleComponentSkus(variantIds) {
    if (!variantIds || variantIds.length === 0) return new Map();

    const query = `
      query GetBundlesComponents($variantIds: [ID!]!) {
        nodes(ids: $variantIds) {
          ... on ProductVariant {
            id
            sku
            requiresComponents
            components(first: 10) {
              nodes {
                productVariant {
                  sku
                  product {
                    productType
                    tags
                  }
                }
                quantity
              }
            }
          }
        }
      }
    `;

    const gids = variantIds.map((id) => `gid://shopify/ProductVariant/${id}`);
    const headers = { 'Content-Type': 'application/json' };
    if (window.STOREFRONT_ACCESS_TOKEN) {
      headers['X-Shopify-Storefront-Access-Token'] = window.STOREFRONT_ACCESS_TOKEN;
    }

    const res = await fetch(STOREFRONT_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, variables: { variantIds: gids } }),
    });

    const data = await res.json();

    if (data.errors && data.errors.length > 0) {
      console.error('[StockCheck] Storefront API errors. Si es por scope, configurar window.STOREFRONT_ACCESS_TOKEN:', data.errors);
      return new Map();
    }

    const result = new Map();
    for (const node of data?.data?.nodes || []) {
      if (!node || !node.id) continue;
      const variantId = node.id.split('/').pop();
      const components = node.components?.nodes || [];

      const machineComponents = components.filter(
        (c) => (c.productVariant?.product?.productType || '').trim().toLowerCase() !== SERVICE_PRODUCT_TYPE
      );

      if (machineComponents.length === 0) {
        console.error(
          `[StockCheck] bundle ${node.sku}: todos los componentes son servicios. No se puede identificar la máquina.`,
          { components }
        );
        continue;
      }

      if (machineComponents.length > 1) {
        console.warn(
          `[StockCheck] bundle ${node.sku}: ${machineComponents.length} componentes no-servicio. Se usa el primero. Revisar catálogo.`,
          { machineComponents }
        );
      }

      const machineSku = machineComponents[0].productVariant?.sku;
      if (machineSku) {
        result.set(variantId, machineSku);
      } else {
        console.warn('[StockCheck] variant', variantId, 'componente máquina sin SKU. Node:', node);
      }
    }
    return result;
  }

  // ==================== Payload ====================

  /**
   * Agrega items del carrito por SKU para validación de stock contra SAP.
   *
   * @param {Array} cartItems
   * @returns {Promise<Array<{sku:string, quantity:number}>>}
   */
  async function aggregateBySku(cartItems) {
    // Step 1: identificar bundles que necesitan resolución de componentes.
    const bundleVariantIds = (cartItems || [])
      .filter((item) => FFType.isBundle(item) && item.variant_id && !(item.properties && item.properties.sku))
      .map((item) => item.variant_id);

    // Step 2: resolver via Storefront API (una sola llamada batched).
    const componentSkuByVariantId = await resolveBundleComponentSkus(bundleVariantIds);

    // Reseteamos el mapa de display info al inicio de cada validación.
    bundleDisplayBySku = new Map();

    // Step 3: agregación.
    const map = new Map();
    for (const item of cartItems || []) {
      let sku = (item.sku || '').trim();
      if (!sku) continue;
      // Productos independientes marcados como Servicio en Shopify (mantenimiento,
      // instalacion, garantia): no tienen stock fisico que validar contra SAP.
      if (FFType.isService(item)) continue;
      // Hijos de bundle (servicios): llevan properties.sku apuntando al padre.
      if (item.properties && item.properties.sku) continue;
      // Bundles nativos: sustituir el SKU del bundle por el del variant físico.
      if (FFType.isBundle(item)) {
        const inner = componentSkuByVariantId.get(String(item.variant_id));
        if (inner) {
          // Guardamos el título y SKU del bundle para mostrarlos en el popup
          // de error en lugar del SKU interno (que el cliente no reconoce).
          bundleDisplayBySku.set(inner, {
            title: item.product_title || item.title || '',
            bundleSku: item.sku || '',
          });
          sku = inner;
        } else {
          console.warn(`[StockCheck] bundle ${sku} (variant ${item.variant_id}) sin componente resuelto. Se envía SKU del bundle como fallback.`);
        }
      }
      const prev = map.get(sku) || 0;
      map.set(sku, prev + (item.quantity || 0));
    }

    return Array.from(map, ([sku, quantity]) => ({ sku, quantity }));
  }

  /**
   * Enriquece blockingItems con info legible (título y SKU del bundle del
   * carrito) para que el popup muestre lo que el cliente reconoce, en lugar
   * del SKU interno del variant.
   */
  function enrichBlockingItems(blockingItems) {
    return (blockingItems || []).map((item) => {
      const display = bundleDisplayBySku.get(item.sku);
      if (!display) return item;
      return {
        ...item,
        displayTitle: display.title || undefined,
        displaySku: display.bundleSku || undefined,
      };
    });
  }

  function chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  async function fetchCart() {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    return res.json();
  }

  // ==================== Validación remota ====================

  async function postChunk(items) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Valida en chunks secuenciales. Chunks degraded o con fallo de red se ignoran
   * (sus items quedan exentos del bloqueo). Devuelve blockingItems: items ok:false
   * provenientes de chunks NO degraded.
   */
  async function validateChunks(items) {
    const chunks = chunk(items, CHUNK_SIZE);
    const blockingItems = [];

    for (const c of chunks) {
      let response;
      try {
        response = await postChunk(c);
      } catch (err) {
        console.warn('[StockCheck] chunk request failed, treating as degraded:', err);
        continue;
      }
      if (!response || response.degraded) continue;
      if (!Array.isArray(response.items)) continue;
      for (const item of response.items) {
        if (item && item.ok === false) blockingItems.push(item);
      }
    }

    return { canCheckout: blockingItems.length === 0, blockingItems };
  }

  /**
   * Devuelve la lista de items bloqueados que SIGUEN violando el stock con el
   * carrito actual. Un item deja de aparecer si: (a) ya no está en el carrito,
   * o (b) su cantidad actual cabe en `available`.
   * `requested` se actualiza a la qty actual del carrito para que el popup
   * refleje el estado vigente.
   */
  async function currentConflictingItems(cartItems) {
    const currentBySku = new Map(
      (await aggregateBySku(cartItems || [])).map((i) => [i.sku, i.quantity])
    );
    const conflicts = [];
    for (const blocked of state.blockingItems) {
      const currentQty = currentBySku.get(blocked.sku) || 0;
      if (currentQty === 0) continue;
      if (currentQty > (blocked.available || 0)) {
        // Spread preserva displayTitle/displaySku del snapshot bloqueante.
        conflicts.push({
          ...blocked,
          requested: currentQty,
          ok: false,
        });
      }
    }
    return conflicts;
  }

  function allCheckoutButtons() {
    return document.querySelectorAll(
      '[data-ct-stock-checkout-button], #cart-modal-button'
    );
  }

  function lockUI() {
    document.body.classList.add('ct-stock-check-in-flight');
    allCheckoutButtons().forEach((btn) => {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('loading');
      const spinner = btn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    });
  }

  function unlockUI() {
    document.body.classList.remove('ct-stock-check-in-flight');
    allCheckoutButtons().forEach((btn) => {
      btn.removeAttribute('disabled');
      btn.classList.remove('loading');
      const spinner = btn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    });
  }

  function hasMixedBundleAndVentilador(cartItems) {
    let hasBundle = false;
    let hasVentilador = false;
    for (const item of cartItems || []) {
      // isBundle primero: un bundle nunca debe contar como ventilador aunque
      // su product_type lo fuera.
      if (FFType.isBundle(item)) hasBundle = true;
      else if (FFType.isVentilador(item)) hasVentilador = true;
      if (hasBundle && hasVentilador) return true;
    }
    return false;
  }

  function openMixedCartPopup() {
    const modal = document.querySelector('ct-stock-error-modal');
    if (modal && typeof modal.openWithMessage === 'function') {
      modal.openWithMessage({
        title: modal.dataset.ctTitleMixedCart,
        message: modal.dataset.ctMessageMixedCart,
      });
    } else {
      console.warn('[StockCheck] <ct-stock-error-modal> sin openWithMessage; soft block sin popup');
    }
  }

  function openPopup(blockingItems) {
    const modal = document.querySelector('ct-stock-error-modal');
    if (modal && typeof modal.openWithItems === 'function') {
      modal.openWithItems(blockingItems);
    } else {
      console.warn('[StockCheck] <ct-stock-error-modal> no encontrado en el DOM');
    }
  }

  /**
   * @param {(cart?: Object) => void} proceedFn Callback al pasar la validación.
   *   Recibe el carrito ya leído de /cart.js para que el consumidor no tenga
   *   que repetir el fetch; es undefined en los caminos fail-open donde la
   *   lectura del carrito falló.
   */
  async function interceptCheckout(proceedFn) {
    if (state.status === 'in-flight') return;

    state.status = 'in-flight';
    lockUI();

    let cart;
    try {
      try {
        cart = await fetchCart();
      } catch (err) {
        console.warn('[StockCheck] no se pudo leer el carrito, fail-open:', err);
        state.status = 'idle';
        unlockUI();
        proceedFn();
        return;
      }

      // Soft block bundle + ventilador: corre antes del stock-check y no toca
      // state.blockingItems; status vuelve a idle para que el siguiente click
      // re-valide el carrito ya editado.
      if (hasMixedBundleAndVentilador(cart.items)) {
        state.status = 'idle';
        unlockUI();
        openMixedCartPopup();
        return;
      }

      // Setting del theme (Carrito). Solo un false explícito la apaga: si falta
      // la variable se mantiene la validación.
      if (window.ctStockCheckEnabled === false) {
        state.status = 'idle';
        state.blockingItems = [];
        try {
          proceedFn(cart);
        } catch (err) {
          console.warn('[StockCheck] proceedFn lanzó:', err);
        } finally {
          unlockUI();
        }
        return;
      }

      if (state.blockingItems.length > 0) {
        const conflicts = await currentConflictingItems(cart.items);
        if (conflicts.length > 0) {
          state.status = 'blocked';
          unlockUI();
          openPopup(conflicts);
          return;
        }
      }

      const items = await aggregateBySku(cart.items);
      if (items.length === 0) {
        state.status = 'idle';
        state.blockingItems = [];
        unlockUI();
        proceedFn(cart);
        return;
      }

      const { canCheckout, blockingItems } = await validateChunks(items);
      if (canCheckout) {
        state.status = 'idle';
        state.blockingItems = [];

        try {
          proceedFn(cart);
        } catch (err) {
          console.warn('[StockCheck] proceedFn lanzó:', err);
        } finally {
          unlockUI();
        }
      } else {
        const enriched = enrichBlockingItems(blockingItems);
        state.status = 'blocked';
        state.blockingItems = enriched;
        unlockUI();
        openPopup(enriched);
      }
    } catch (err) {
      console.warn('[StockCheck] validación falló, fail-open:', err);
      state.status = 'idle';
      state.blockingItems = [];
      unlockUI();
      proceedFn(cart);
    }
  }

  function registerCheckoutButton(btn) {
    if (btn) btn.setAttribute('data-ct-stock-checkout-button', '');
  }

  function installClickDelegation() {
    document.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-ct-stock-checkout-button]');
      if (!btn) return;
      event.preventDefault();
      interceptCheckout(() => {
        window.location.href = '/checkout';
      });
    });
  }

  function init() {
    installClickDelegation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.StockCheck = {
    interceptCheckout,
    registerCheckoutButton,
    _state: state,
  };
})();
